import { QuestionMarkCircleIcon } from "@heroicons/react/20/solid";
import { type ReactNode, useEffect, useState } from "react";
import { OtsFormDialog } from "../components/OtsFormDialog";
import { OtsSelectableList } from "../components/OtsSelectableList";
import { useAppDensity } from "../app/AppContext";
import type { OtsBrand, OtsColorSchemePreference } from "../theme/OtsTheme";

export type ChoiceDialogOption = {
  id: string;
  label: ReactNode;
  /** What choosing it does, under the label. */
  description?: ReactNode;
  /** The submit button's text once it is picked, e.g. "Replace visit". */
  actionLabel?: string;
  disabled?: boolean;
};

export type ChoiceDialogProps = {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  options: ChoiceDialogOption[];
  /** Picked when it opens; nothing is, without one, until someone chooses. */
  defaultChoice?: string;
  onChoose: (id: string) => void;
  onCancel: () => void;
  isWorking?: boolean;
  /** A failed attempt; the dialog stays open to choose again. */
  error?: ReactNode;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/**
 * Asks which of a few ways to go, when there is more than yes or no: merge or replace, keep this site or that one.
 * The options are cards; the button takes the picked option's action.
 */
export const ChoiceDialog = ({
  open,
  title,
  description,
  icon,
  options,
  defaultChoice,
  onChoose,
  onCancel,
  isWorking = false,
  error,
  brand,
  colorScheme,
}: ChoiceDialogProps) => {
  const compact = useAppDensity() === "compact";
  const [choice, setChoice] = useState(defaultChoice);
  // Each opening starts from the default; last time's pick doesn't carry over.
  useEffect(() => {
    if (open) setChoice(defaultChoice);
  }, [open, defaultChoice]);
  const picked = options.find((option) => option.id === choice);

  return (
    <OtsFormDialog
      open={open}
      onClose={onCancel}
      onSubmit={() => picked && !picked.disabled && onChoose(picked.id)}
      title={title}
      description={description}
      icon={icon ?? <QuestionMarkCircleIcon/>}
      formLabel={typeof title === "string" ? title : "Choose"}
      submitLabel={picked?.actionLabel ?? "Continue"}
      submittingLabel={picked?.actionLabel ? `${picked.actionLabel}…` : "Working…"}
      submitDisabled={!picked || picked.disabled}
      submitting={isWorking}
      error={error}
      size={compact ? "sm" : "md"}
      compact={compact}
      brand={brand}
      colorScheme={colorScheme}>
      <OtsSelectableList
        ariaLabel={typeof title === "string" ? title : "Choices"}
        selectedId={choice}
        onSelect={setChoice}
        compact={compact}
        items={options.map((option) => ({
          id: option.id,
          disabled: option.disabled,
          content: (
            <span className="block text-left">
              <span className="block font-semibold">{option.label}</span>
              {option.description && <span className="mt-0.5 block text-xs text-[var(--ots-text-muted)]">{option.description}</span>}
            </span>
          ),
        }))}/>
    </OtsFormDialog>
  );
};
